import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const CTAContact = () => {
  const [formData, setFormData] = useState({
    bedrijfsnaam: '',
    contactpersoon: '',
    sector: '',
    aantalMedewerkers: '',
    startdatum: '',
    aanvullendeVereisten: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e?.target?.name]: e?.target?.value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setSubmitted(true);
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5" style={{ background: 'radial-gradient(circle at 50% 50%, #00E5A0 0%, transparent 65%)' }} />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">

        {/* Top CTA bar */}
        <motion.div
          className="p-8 md:p-12 mb-px text-center"
          style={{ background: '#0D0D0D', border: '1px solid #1A1A1A' }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-xs font-body font-medium tracking-[0.2em] uppercase mb-4" style={{ color: '#00E5A0' }}>
            KLAAR VOOR INZET?
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6">
            START<br />UW<br /><span style={{ color: '#00E5A0' }}>ZOEKTOCHT</span>
          </h2>
          <p className="text-base max-w-xl mx-auto mb-8 leading-relaxed" style={{ color: '#888888' }}>
            Vertel ons wat u nodig hebt. Ons team reageert binnen 2 werkuren met een op maat gemaakt voorstel en een initiële kandidatenlijst — geen verplichtingen, geen verborgen kosten.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <Link to="/login">
              <motion.button
                className="flex items-center gap-2 px-8 py-4 font-body font-semibold text-sm tracking-wide rounded-sm"
                style={{ background: '#00E5A0', color: '#0A0A0A' }}
                whileHover={{ scale: 1.04, background: '#00ffb3' }}
                whileTap={{ scale: 0.96 }}
              >
                PERSONEEL AANVRAGEN
                <Icon name="ArrowRight" size={18} color="#0A0A0A" />
              </motion.button>
            </Link>
            <a href="tel:+3200000000">
              <motion.button
                className="flex items-center gap-2 px-8 py-4 font-body font-semibold text-sm tracking-wide rounded-sm border text-white"
                style={{ borderColor: '#2A2A2A', background: 'transparent' }}
                whileHover={{ borderColor: '#00E5A0', color: '#00E5A0' }}
                whileTap={{ scale: 0.96 }}
              >
                <Icon name="Phone" size={18} />
                BEL ONS NU
              </motion.button>
            </a>
          </div>

          {/* Guarantees bar */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
            {[
              { icon: 'Clock', label: '2u Reactietijd' },
              { icon: 'Zap', label: '72u Inzet-SLA' },
              { icon: 'EuroIcon', label: '0 Verborgen kosten' },
            ]?.map((g, i) => (
              <div key={i} className="flex items-center gap-2">
                <Icon name={g?.icon === 'EuroIcon' ? 'BadgeEuro' : g?.icon} size={16} color="#00E5A0" />
                <span className="text-sm font-body font-medium" style={{ color: '#AAAAAA' }}>{g?.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Contact form */}
        <motion.div
          className="p-8 md:p-12"
          style={{ background: '#0D0D0D', border: '1px solid #1A1A1A', borderTop: 'none' }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <p className="text-xs font-body font-medium tracking-[0.2em] uppercase mb-4" style={{ color: '#00E5A0' }}>
                NEEM CONTACT OP
              </p>
              <h3 className="text-3xl md:text-4xl font-heading font-bold text-white leading-tight mb-4">
                LATEN WE<br />PRATEN OVER<br /><span style={{ color: '#00E5A0' }}>PERSONEEL</span>
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: '#666666' }}>
                Vul het formulier in en ons team neemt binnen 2 werkuren contact met u op met een gepersonaliseerd voorstel.
              </p>
            </div>

            <div>
              {submitted ? (
                <motion.div
                  className="flex flex-col items-center justify-center h-full py-12 text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(0,229,160,0.15)', border: '1px solid rgba(0,229,160,0.3)' }}>
                    <Icon name="CheckCircle" size={32} color="#00E5A0" />
                  </div>
                  <h4 className="text-xl font-heading font-bold text-white mb-2">Aanvraag ontvangen!</h4>
                  <p className="text-sm" style={{ color: '#888888' }}>Ons team reageert binnen 2 werkuren.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {[
                    { name: 'bedrijfsnaam', label: 'Bedrijfsnaam »', type: 'text' },
                    { name: 'contactpersoon', label: 'Contactpersoon »', type: 'text' },
                    { name: 'sector', label: 'Sector »', type: 'text' },
                    { name: 'aantalMedewerkers', label: 'Aantal benodigde medewerkers »', type: 'number' },
                    { name: 'startdatum', label: 'Gewenste startdatum »', type: 'date' },
                  ]?.map((field) => (
                    <div key={field?.name}>
                      <label className="block text-xs font-body font-medium mb-1.5" style={{ color: '#888888' }}>
                        {field?.label}
                      </label>
                      <input
                        type={field?.type}
                        name={field?.name}
                        value={formData?.[field?.name]}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm font-body text-white rounded-sm outline-none transition-all duration-200"
                        style={{
                          background: '#141414',
                          border: '1px solid #2A2A2A',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = '#00E5A0'; }}
                        onBlur={(e) => { e.target.style.borderColor = '#2A2A2A'; }}
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs font-body font-medium mb-1.5" style={{ color: '#888888' }}>
                      Aanvullende vereisten »
                    </label>
                    <textarea
                      name="aanvullendeVereisten"
                      value={formData?.aanvullendeVereisten}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-4 py-3 text-sm font-body text-white rounded-sm outline-none transition-all duration-200 resize-none"
                      style={{ background: '#141414', border: '1px solid #2A2A2A' }}
                      onFocus={(e) => { e.target.style.borderColor = '#00E5A0'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#2A2A2A'; }}
                    />
                  </div>
                  <motion.button
                    type="submit"
                    className="w-full py-4 font-body font-semibold text-sm tracking-wide rounded-sm mt-2"
                    style={{ background: '#00E5A0', color: '#0A0A0A' }}
                    whileHover={{ scale: 1.02, background: '#00ffb3' }}
                    whileTap={{ scale: 0.98 }}
                  >
                    PERSONEEL AANVRAGEN →
                  </motion.button>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTAContact;
